import { TherapyTarget } from 'domains/signup/data/types';
import { TherapyTargetValue } from 'domains/signupDirect/misc/constants';
import { union } from 'lodash';
import { useMemo } from 'react';

export type SignupClinicalArea =
  | 'Adult Receptive-Expressive Language Disorder'
  | 'Pediatric Articulation/Phonological Disorder'
  | 'Pediatric/Adolescent Social-Pragmatics'
  | 'Gender Affirming Voice'
  | 'Augmentative and alternative communication'
  | 'Acquired Apraxia'
  | 'Adult Articulation'
  | 'Aphasia'
  | 'Adult Fluency/Stuttering'
  | 'Pediatric Dysphagia/Sensory Feeding'
  | 'Voice'
  | 'Parkinsons'
  | 'Early Language Development'
  | 'Pediatric Fluency/Stuttering'
  | 'Autism Spectrum Disorder'
  | 'Public/Professional Speaking'
  | 'Pediatric Receptive-Expressive Language Disorder'
  | 'Accent Modification'
  | 'Adult Cognition'
  | 'Other';

type PediatricConcern =
  | 'Pronouncing sounds clearly'
  | 'Using words to communicate'
  | 'Understanding others'
  | 'Speech or language delay'
  | 'Stuttering'
  | 'Lisp'
  | 'Autism'
  | 'Socializing with others'
  | 'Feeding or swallowing'
  | 'Assisted communication device'
  | 'Other';

type AdultConcern =
  | 'Expressing thoughts clearly'
  | 'Clarity of speech'
  | 'Understanding language'
  | 'Stuttering'
  | 'Voice clarity'
  | 'Voice alignment with gender identity'
  | 'Concentration and memory'
  | 'Acquired apraxia'
  | 'Neurodegenerative disorder'
  | 'Post-stroke'
  | 'Post-traumatic brain injury'
  | 'Professional communication skills'
  | 'Accent modification'
  | 'Other';

export interface PediatricConcernOption {
  /** Used to match concerns coming from URL */
  id: string;
  /** Unique ID */
  key: PediatricConcern;
  clinicalAreas: SignupClinicalArea[];
  allowEval: boolean;
  allowAnySLP: boolean;
}

export interface AdultConcernOption {
  /** Used to match concerns coming from URL */
  id: string;
  /** Unique ID */
  key: AdultConcern;
  clinicalAreas: SignupClinicalArea[];
  allowEval: boolean;
  allowAnySLP: boolean;
}

export const SIGNUP_CONCERNS_PEDIATRIC: PediatricConcernOption[] = [
  {
    id: '1',
    key: 'Pronouncing sounds clearly',
    clinicalAreas: ['Pediatric Articulation/Phonological Disorder'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    id: '2',
    key: 'Using words to communicate',
    clinicalAreas: ['Early Language Development', 'Pediatric Receptive-Expressive Language Disorder'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    id: '3',
    key: 'Understanding others',
    clinicalAreas: ['Pediatric Receptive-Expressive Language Disorder'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    id: '4',
    key: 'Speech or language delay',
    clinicalAreas: [
      'Early Language Development',
      'Pediatric Receptive-Expressive Language Disorder',
      'Pediatric Articulation/Phonological Disorder',
    ],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    id: '5',
    key: 'Stuttering',
    clinicalAreas: ['Pediatric Fluency/Stuttering'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    id: '6',
    key: 'Lisp',
    clinicalAreas: ['Pediatric Articulation/Phonological Disorder'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    id: '7',
    key: 'Autism',
    clinicalAreas: ['Autism Spectrum Disorder'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    id: '8',
    key: 'Socializing with others',
    clinicalAreas: ['Pediatric/Adolescent Social-Pragmatics'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    id: '9',
    key: 'Feeding or swallowing',
    clinicalAreas: ['Pediatric Dysphagia/Sensory Feeding'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    id: '10',
    key: 'Assisted communication device',
    clinicalAreas: ['Augmentative and alternative communication'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    id: '11',
    key: 'Other',
    clinicalAreas: ['Other'],
    allowEval: true,
    allowAnySLP: true,
  },
];

export const SIGNUP_CONCERNS_ADULT: AdultConcernOption[] = [
  {
    id: '12',
    key: 'Expressing thoughts clearly',
    clinicalAreas: ['Adult Receptive-Expressive Language Disorder'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    id: '13',
    key: 'Clarity of speech',
    clinicalAreas: ['Adult Articulation'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    id: '14',
    key: 'Understanding language',
    clinicalAreas: ['Adult Receptive-Expressive Language Disorder'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    id: '15',
    key: 'Stuttering',
    clinicalAreas: ['Adult Fluency/Stuttering'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    id: '16',
    key: 'Voice clarity',
    clinicalAreas: ['Voice'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    id: '17',
    key: 'Voice alignment with gender identity',
    clinicalAreas: ['Gender Affirming Voice'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    id: '18',
    key: 'Concentration and memory',
    clinicalAreas: ['Adult Cognition'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    id: '19',
    key: 'Acquired apraxia',
    clinicalAreas: ['Acquired Apraxia'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    id: '20',
    key: 'Neurodegenerative disorder',
    clinicalAreas: ['Parkinsons'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    id: '21',
    key: 'Post-stroke',
    clinicalAreas: ['Aphasia'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    id: '22',
    key: 'Post-traumatic brain injury',
    clinicalAreas: ['Aphasia'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    id: '23',
    key: 'Professional communication skills',
    clinicalAreas: ['Public/Professional Speaking'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    id: '24',
    key: 'Accent modification',
    clinicalAreas: ['Accent Modification'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    id: '25',
    key: 'Other',
    clinicalAreas: ['Other'],
    allowEval: false,
    allowAnySLP: false,
  },
];

/**
 * If user's concerns are ONLY some of these list, force consultation
 */
export const SIGNUP_CONCERNS_ADULT_COMBINATION_EXCEPTIONS: (AdultConcern | PediatricConcern)[] = [
  'Accent modification',
  'Professional communication skills',
];

export const useCommunicationConcernOptions = ({
  currentKeys,
  therapyTarget,
}: {
  currentKeys?: string[];
  therapyTarget?: TherapyTargetValue;
}) => {
  return useMemo(() => {
    if (!therapyTarget || !currentKeys) return undefined;

    if (therapyTarget === 'child') {
      return SIGNUP_CONCERNS_PEDIATRIC.filter(v => currentKeys.includes(v.key));
    }

    return SIGNUP_CONCERNS_ADULT.filter(v => currentKeys.includes(v.key));
  }, [currentKeys, therapyTarget]);
};

export const getAreasByConcernKeys = (clinicalConcernKeys: string[], target: TherapyTarget): SignupClinicalArea[] => {
  let resultAreas: SignupClinicalArea[] = [];

  clinicalConcernKeys.forEach(key => {
    let foundAreas: SignupClinicalArea[] | undefined;
    if (target === 'child') {
      foundAreas = SIGNUP_CONCERNS_PEDIATRIC.find(c => c.key === key)?.clinicalAreas;
    } else {
      foundAreas = SIGNUP_CONCERNS_ADULT.find(c => c.key === key)?.clinicalAreas;
    }

    resultAreas = union(resultAreas, foundAreas);
  });

  return resultAreas;
};

/** Returns clinical areas, to by used for API filters */
export const getFilteringAreasByConcernKeys = (
  clinicalConcernKeys: string[],
  target: TherapyTarget,
): SignupClinicalArea[] => {
  let resultAreas: SignupClinicalArea[] = [];

  clinicalConcernKeys.forEach(key => {
    let concern: PediatricConcernOption | AdultConcernOption | undefined;
    if (target === 'child') {
      concern = SIGNUP_CONCERNS_PEDIATRIC.find(c => c.key === key);
    } else {
      concern = SIGNUP_CONCERNS_ADULT.find(c => c.key === key);
    }

    // Only filter by those areas that don't allow ANY SLP
    if (concern?.allowAnySLP === false) {
      resultAreas = union(resultAreas, concern.clinicalAreas);
    }
  });

  return resultAreas;
};

export const mergeCommunicationConcerns = (clinicalConcernKeys: string[], detectedAreas: SignupClinicalArea[]) => {
  let result = '';

  result += `Clinical Concerns \n ${clinicalConcernKeys.join('\n')}\n\n`;

  result += `Detected Areas \n ${detectedAreas.join('\n')}`;
  return result;
};

export const getCommunicationConcernOptionsFromKeys = (keys: string[], target: TherapyTarget) => {
  if (target === 'child') {
    return SIGNUP_CONCERNS_PEDIATRIC.filter(v => keys.includes(v.key));
  }

  return SIGNUP_CONCERNS_ADULT.filter(v => keys.includes(v.key));
};

/** Used for resuming existing leadID that has data about areas but not the concerns themselves */
export const reverseConcernsFromAreas = (areas: SignupClinicalArea[], target: TherapyTarget): string[] => {
  const resultConcernKeys: string[] = [];

  areas.forEach(area => {
    let concern: PediatricConcernOption | AdultConcernOption | undefined;
    if (target === 'child') {
      concern = SIGNUP_CONCERNS_PEDIATRIC.find(c => c.clinicalAreas.includes(area));
    } else {
      concern = SIGNUP_CONCERNS_ADULT.find(c => c.clinicalAreas.includes(area));
    }

    if (concern && !resultConcernKeys.includes(concern.key)) {
      resultConcernKeys.push(concern.key);
    }
  });

  return resultConcernKeys;
};
